import * as T from 'three';

/**
 * Shared, frame-updated shader inputs. Every stylized material references these
 * exact uniform objects, so one write per frame drives the whole world.
 */
export const atmosphere = {
    time: { value: 0 },
    /** Player focus point in camera view space (written by the camera each frame). */
    focus: { value: new T.Vector3(0, 0, -8) },
    /** 0 disables camera see-through fading (editor, photo views). */
    fade: { value: 1 },
    /** Foliage sway amplitude in metres. */
    sway: { value: .07 },
    /** Temporal offset of the see-through noise; 0 keeps it static (reduced motion). */
    frame: { value: 0 },
    /** Sun direction in view space (foliage translucency). */
    sunView: { value: new T.Vector3(0, 1, 0) },
    /**
     * Distance grade (Bible §3): x = end of the warm, higher-contrast foreground, y = start of the
     * cool/soft distance, z = strength (0 disables), w = end of the cool ramp. Metres from the lens.
     */
    grade: { value: new T.Vector4(22, 40, 1, 160) },
    /** Horizon haze in display (sRGB) space; matches the fog colour and the sky's horizon. */
    hazeColor: { value: new T.Vector3(.66, .82, .88) },
    /** x/y = haze start/end distance for far scenery that does not use scene fog. */
    hazeRange: { value: new T.Vector2(60, 520) },
};

/** Sets the shared horizon colour (fog, sky horizon and far-scenery haze all agree). */
export function setHorizon(color: T.Color) {
    const c = color.clone().convertLinearToSRGB();
    atmosphere.hazeColor.value.set(c.r, c.g, c.b);
}

export type StyleOptions = {
    /** Fresnel rim colour + strength + exponent. */
    rim?: { color: T.ColorRepresentation; strength: number; power?: number };
    /**
     * Camera see-through: 'full' clears geometry at the lens and opens a see-through tube from the
     * lens to just short of the monkey (decor, decks). 'grip' stops the tube further from the
     * monkey so the branch in its hands (and the one it is about to catch) never dissolves.
     * 'cluster' fades whole instanced foliage clusters uniformly instead of punching holes.
     */
    fade?: 'full' | 'grip' | 'cluster';
    /** Wind sway in the vertex shader (world-space batched or instanced foliage). */
    sway?: boolean;
    /** Distance colour grade; defaults to on for world materials (anything with `fade`). */
    grade?: boolean;
    /**
     * Sculpted foliage: keep the authored volume normals on both faces (soft clump shading)
     * and add a cheap sun-through-leaves glow when looking toward the sun.
     */
    foliage?: boolean;
    /** Far scenery without scene fog: haze toward the horizon colour, capped at this amount. */
    haze?: number;
};

/**
 * Interleaved gradient noise (Jimenez 2014): an even, high-frequency threshold with no visible
 * grid, unlike a 4×4 Bayer matrix. `canopyFrame` rotates it each frame so the remaining grain
 * integrates into translucency instead of reading as a screen door.
 */
const NOISE = `float canopyNoise() {
    vec2 p = gl_FragCoord.xy + canopyFrame * vec2(47.0, 17.0);
    return fract(52.9829189 * fract(dot(p, vec2(.06711056, .00583715))));
}`;
/** Shared see-through volume: near-lens sphere plus a tube from the lens to the monkey. */
const TUBE = (tailNear: string, tailFar: string) => `float canopyTube(vec3 fp, float pad) {
    float focusLength = max(length(canopyFocus), .001);
    vec3 focusDir = canopyFocus / focusLength;
    float along = dot(fp, focusDir);
    float perp = max(0.0, length(fp - focusDir * along) - pad);
    float nearFade = 1.0 - smoothstep(.7, 1.9, max(0.0, length(fp) - pad));
    float tubeRadius = mix(.4, .95, clamp(along / focusLength, 0.0, 1.0));
    float tube = (1.0 - smoothstep(tubeRadius * .7, tubeRadius, perp))
        * step(0.0, along + pad) * (1.0 - smoothstep(focusLength - ${tailNear}, focusLength - ${tailFar}, along - pad * .5));
    return max(nearFade, tube) * canopyFade;
}`;

const fadeMaterials = new Set<T.Material>();
let coverage = false;

/**
 * Adds CANOPY!'s cheap stylization to a lit three.js material in one onBeforeCompile:
 * rim separation, camera see-through, foliage sway/volume shading and the distance grade.
 */
export function stylize<M extends T.Material>(material: M, options: StyleOptions): M {
    const rimColor = new T.Color(options.rim?.color ?? '#ffffff');
    const rim = { color: { value: rimColor }, strength: { value: options.rim?.strength ?? 0 }, power: { value: options.rim?.power ?? 2.5 } };
    const grade = options.grade ?? !!options.fade;
    material.userData.rim = rim;
    material.onBeforeCompile = shader => {
        const u = shader.uniforms;
        u.canopyTime = atmosphere.time;
        u.canopySway = atmosphere.sway;
        u.canopyFocus = atmosphere.focus;
        u.canopyFade = atmosphere.fade;
        u.canopyFrame = atmosphere.frame;
        u.canopySunView = atmosphere.sunView;
        u.canopyGrade = atmosphere.grade;
        u.canopyHazeColor = atmosphere.hazeColor;
        u.canopyHazeRange = atmosphere.hazeRange;
        u.canopyHazeMax = { value: options.haze ?? 0 };
        u.canopyRimColor = rim.color;
        u.canopyRimStrength = rim.strength;
        u.canopyRimPower = rim.power;
        const tail = options.fade === 'grip' ? ['1.65', '1.3'] : ['1.1', '.7'];
        const fade = options.fade ? `uniform vec3 canopyFocus;\nuniform float canopyFade;\n${TUBE(tail[0], tail[1])}` : '';
        // ---- vertex ----
        let vertexHeader = 'uniform float canopyTime;\nuniform float canopySway;\n';
        if (options.fade === 'cluster') vertexHeader += `${fade}\nvarying float vCanopyCluster;\n`;
        shader.vertexShader = shader.vertexShader.replace('#include <common>', `#include <common>\n${vertexHeader}`);
        if (options.sway)
            shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
                {
                    #ifdef USE_INSTANCING
                    vec3 sw = (instanceMatrix * vec4(position, 1.0)).xyz;
                    float reach = clamp(position.y + .6, 0.0, 1.6);
                    #else
                    vec3 sw = position;
                    float reach = 1.0;
                    #endif
                    float phase = canopyTime * 1.45 + sw.x * .29 + sw.z * .23;
                    float gust = .65 + .35 * sin(canopyTime * .37 + sw.x * .021);
                    transformed.x += sin(phase) * canopySway * gust * reach;
                    transformed.z += cos(phase * .83 + sw.y * .4) * canopySway * .7 * gust * reach;
                    transformed.y += sin(phase * 1.7) * canopySway * .35 * reach;
                }`);
        if (options.fade === 'cluster')
            shader.vertexShader = shader.vertexShader.replace('#include <project_vertex>', `#include <project_vertex>
                #ifdef USE_INSTANCING
                {
                    // Fade the whole clump as one: a smooth translucent mass, never a punched hole.
                    vec4 canopyCenter = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
                    float canopyRadius = length(instanceMatrix[0].xyz) * length(modelMatrix[0].xyz);
                    vCanopyCluster = canopyTube(canopyCenter.xyz, canopyRadius * .85);
                    // Fully cleared clumps are dropped in the vertex stage (no fragment cost). Without
                    // the per-pixel fade (Light preset) a clump in the way pops out whole instead.
                    #ifdef CANOPY_FADE
                    if (vCanopyCluster > .97) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
                    #else
                    if (vCanopyCluster > .45) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
                    #endif
                }
                #else
                vCanopyCluster = -1.0; // merged (non-instanced) foliage fades per pixel
                #endif`);
        // ---- fragment ----
        const header = [
            'uniform float canopyFrame;\nuniform vec3 canopySunView;\nuniform vec4 canopyGrade;\nuniform vec3 canopyHazeColor;\nuniform vec2 canopyHazeRange;\nuniform float canopyHazeMax;',
            options.rim ? 'uniform vec3 canopyRimColor;\nuniform float canopyRimStrength;\nuniform float canopyRimPower;' : '',
            options.fade ? `${options.fade === 'cluster' ? `varying float vCanopyCluster;\n${fade}` : fade}\n${NOISE}` : '',
        ].join('\n');
        shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `#include <common>\n${header}`);
        if (options.fade)
            shader.fragmentShader = shader.fragmentShader.replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
            #ifdef CANOPY_FADE
            {
                float amount = ${options.fade === 'cluster' ? 'vCanopyCluster >= 0.0 ? vCanopyCluster : canopyTube(-vViewPosition, 0.0)' : 'canopyTube(-vViewPosition, 0.0)'};
                if (amount > .002) {
                    float n = canopyNoise();
                    #ifdef CANOPY_A2C
                    // MSAA alpha-to-coverage: sub-pixel coverage resolves to soft translucency;
                    // a one-level noise offset breaks up the coverage pattern.
                    if (amount > .97) discard;
                    diffuseColor.a = clamp(1.0 - amount + (n - .5) * .22, 0.0, 1.0);
                    #else
                    if (amount > n) discard;
                    #endif
                }
            }
            #endif`);
        if (options.foliage)
            shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_begin>',
                T.ShaderChunk.normal_fragment_begin.replace('normal *= faceDirection;', '/* foliage keeps its volume normal on both faces */'));
        const lighting = [
            options.rim ? `{
                float facing = clamp(dot(normalize(normal), normalize(vViewPosition)), 0.0, 1.0);
                outgoingLight += canopyRimColor * pow(1.0 - facing, canopyRimPower) * canopyRimStrength;
            }` : '',
            options.foliage ? `{
                // Sun through the leaves: warm glow on clumps seen against the light.
                vec3 viewDir = normalize(-vViewPosition);
                float against = pow(clamp(dot(-viewDir, canopySunView), 0.0, 1.0), 4.0);
                float thin = 1.0 - clamp(dot(normal, canopySunView), 0.0, 1.0);
                outgoingLight += diffuseColor.rgb * vec3(1.0, .86, .5) * against * thin * .55;
            }` : '',
        ].join('\n');
        if (lighting.trim())
            shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `${lighting}\n#include <opaque_fragment>`);
        // Grade and haze run in display space, just before fog: a warm, punchy foreground that
        // cools, softens and blues with distance (Bible §3) at a few ALU per pixel, no post pass.
        const post = [
            grade ? `{
                float d = length(vViewPosition);
                vec3 c = gl_FragColor.rgb;
                float l = dot(c, vec3(.299, .587, .114));
                float nearW = (1.0 - smoothstep(canopyGrade.x * .5, canopyGrade.x, d)) * canopyGrade.z;
                float farW = smoothstep(canopyGrade.y, canopyGrade.w, d) * canopyGrade.z;
                vec3 warm = clamp((mix(vec3(l), c, 1.12) - .5) * 1.08 + .5, 0.0, 1.0) * vec3(1.035, 1.0, .93);
                vec3 cool = mix(vec3(l), c, .78) * vec3(.94, 1.0, 1.07);
                cool = mix(cool, canopyHazeColor, .12);
                c = mix(c, warm, nearW);
                gl_FragColor.rgb = mix(c, cool, farW);
            }` : '',
            options.haze ? `{
                float h = smoothstep(canopyHazeRange.x, canopyHazeRange.y, length(vViewPosition)) * canopyHazeMax;
                gl_FragColor.rgb = mix(gl_FragColor.rgb, canopyHazeColor, h);
            }` : '',
        ].join('\n');
        if (post.trim())
            shader.fragmentShader = shader.fragmentShader.replace('#include <fog_fragment>', `${post}\n#include <fog_fragment>`);
    };
    if (options.fade) {
        fadeMaterials.add(material);
        setSeeThrough(material, true);
        applyCoverage(material);
    }
    material.customProgramCacheKey = () => `canopy-${options.rim ? 'r' : ''}${options.fade || ''}${options.sway ? 's' : ''}${grade ? 'g' : ''}${options.foliage ? 'f' : ''}${options.haze ? 'h' : ''}`;
    material.needsUpdate = true;
    return material;
}

/**
 * Toggles the see-through fade at compile time. A shader containing `discard` loses early depth
 * writes, so the light quality preset compiles it out of large-overdraw foliage.
 */
export function setSeeThrough(material: T.Material, enabled: boolean) {
    const defines = (material as T.Material & { defines?: Record<string, string> }).defines ??= {};
    if (enabled === 'CANOPY_FADE' in defines) return;
    if (enabled) defines.CANOPY_FADE = '';
    else delete defines.CANOPY_FADE;
    material.needsUpdate = true;
}

function applyCoverage(material: T.Material) {
    const defines = (material as T.Material & { defines?: Record<string, string> }).defines ??= {};
    if (coverage === material.alphaToCoverage && coverage === 'CANOPY_A2C' in defines) return;
    material.alphaToCoverage = coverage;
    if (coverage) defines.CANOPY_A2C = '';
    else delete defines.CANOPY_A2C;
    material.needsUpdate = true;
}

/**
 * Uses MSAA alpha-to-coverage for the see-through fade when the drawing buffer is multisampled
 * (smooth sub-pixel translucency); otherwise the fade falls back to the noise threshold.
 */
export function setCoverage(enabled: boolean) {
    if (coverage === enabled) return;
    coverage = enabled;
    for (const material of fadeMaterials) applyCoverage(material);
}
